/**
 * Tech-stack authority bar. Goes immediately after the existing trust/proof bar on the
 * landing page. Where the proof-bar communicates COMPLIANCE/RELIABILITY signals (Stripe-secure,
 * EU-hosted, money-back), this bar communicates AUTHORITY by naming specific industry-standard
 * technologies powering the scan engine.
 *
 * Cialdini Principle: Authority — concrete tech names (axe-core 4.10, Playwright, Claude
 * Sonnet) are more credible than vague claims ("AI-powered scanner"). All claims are factually
 * true and verifiable in the codebase.
 *
 * Legal: each technology is NAMED in plain text and represented by a generic inline SVG icon
 * — we don't embed any third-party brand logos that we don't have a license for. The
 * technology *names* themselves are factual descriptions of what the product uses and fall
 * under nominative fair use.
 */

type Badge = {
  /** Headline text — the technology name. Plain text so we never trip a trademark. */
  name: string;
  /** Sub-label — what role it plays in the scan engine. */
  role: string;
  /** Inline SVG path data — purely decorative, generic icon. */
  iconPath: React.ReactNode;
};

const BADGES: Badge[] = [
  {
    name: 'axe-core 4.10',
    role: 'WCAG 2.1 AA engine',
    iconPath: (
      <>
        <polyline points="8 6 2 12 8 18" />
        <polyline points="16 6 22 12 16 18" />
      </>
    ),
  },
  {
    name: 'Microsoft Playwright',
    role: 'Real Chromium browser',
    iconPath: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </>
    ),
  },
  {
    name: 'Anthropic Claude',
    role: 'Plain-English remediation',
    iconPath: (
      <>
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </>
    ),
  },
  {
    name: 'EU model statement',
    role: 'Implementing Decision (EU) 2018/1523',
    iconPath: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </>
    ),
  },
  {
    name: 'Stripe + EU VAT',
    role: 'PCI-compliant checkout',
    iconPath: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </>
    ),
  },
  {
    name: 'Cloudflare R2',
    role: 'EU-jurisdiction storage',
    iconPath: (
      <>
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      </>
    ),
  },
];

export default function TechStackBar() {
  return (
    <section
      className="tech-stack-bar"
      aria-labelledby="tech-stack-heading"
    >
      <h2 id="tech-stack-heading" className="tech-stack-title">
        Powered by the same tools the W3C and Google use
      </h2>
      <ul className="tech-stack-list" role="list">
        {BADGES.map((b) => (
          <li className="tech-stack-item" key={b.name}>
            <span className="tech-stack-icon" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {b.iconPath}
              </svg>
            </span>
            <span className="tech-stack-text">
              <strong className="tech-stack-name">{b.name}</strong>
              <span className="tech-stack-role">{b.role}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
